import axios from "axios";

// Single configured client. `withCredentials` matters for the session cookie
// once the app is served from a different origin than the API.
const api = axios.create({
    baseURL: "/api/extension/v1",
    withCredentials: true,
});

const FALLBACK_MESSAGE = "Something went wrong. Please try again.";

/**
 * Pulls a human-readable message out of an axios error.
 *
 * Every call site used to read `err.response.data.message.en` directly, which
 * throws a second, uncaught TypeError whenever the request failed without a
 * response — offline, DNS failure, timeout, or a proxy returning HTML. That
 * turned a recoverable error into a blank screen.
 */
export function getErrorMessage(error, fallback = FALLBACK_MESSAGE) {
    if (!error) return fallback;

    if (error.code === "ERR_CANCELED" || error.name === "CanceledError") return null;

    if (!error.response) {
        return error.code === "ECONNABORTED"
            ? "The request timed out. Please try again."
            : "Cannot reach the server. Check your connection.";
    }

    const data = error.response.data;
    const message = data && data.message;

    if (typeof message === "string") return message;
    if (message && typeof message.en === "string") return message.en;
    if (typeof data === "string" && data.length < 200) return data;

    if (error.response.status === 401) return "Your session has expired. Please log in again.";
    if (error.response.status === 403) return "You don't have permission to do that.";
    if (error.response.status === 404) return "Not found.";

    return fallback;
}

// True when the failure was an aborted request rather than a real error, so
// callers can stay silent instead of flashing a message the user caused.
export function isCanceled(error) {
    return Boolean(error) && (error.code === "ERR_CANCELED" || error.name === "CanceledError");
}

export default api;
